import React, { useEffect, useState } from 'react';
import { Button, Card, Divider, Modal, Row, Space, Typography, notification } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../../../utils/auth-utils';
import { scopes } from '../../../../authConfig';
import { SlotConfiguration } from '../../../../dto/admin-config-models';
import { getSlotConfigs, postAppointmentsLimitConfiguration } from '../../../../services/admin-page-service';
import { SlotConfigForm } from './slot-config-form';
import { ProductType, SalesChannel } from '../../../../dto/model';
const { Title, Text } = Typography;

export const AdminSlotConfig = () => {
  const { instance, inProgress } = useMsal();
  const [data, setData] = useState<SlotConfiguration[]>([]);
  const [loading, setLoading] = useState(false);
  // using feId (a string) to indicate which config is in edit mode
  const [editingId, setEditingId] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const result = await getSlotConfigs(token.accessToken);
    if (result) {
      setData(result.data.filter((d) => !d.isDeleted));
    } else {
      notification.error({ message: 'Failed to load slot configurations' });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const onSubmit = async (slotConfiguration: SlotConfiguration) => {
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const res = await postAppointmentsLimitConfiguration(token.accessToken, [slotConfiguration]);
      if (res.isSuccess) {
        notification.success({ message: 'Saved' });
        setEditingId(null);
        loadData();
      } else {
        notification.error({ message: 'Save failed', description: res.errors?.join('\n') });
      }
    } catch (err) {
      // validation error
    }
  };

  const onAddNewSlotConfig = () => {
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    const newConfig = {
      id: 0,
      feId: crypto.randomUUID(),
      createdBy: account.username,
      createdDate: new Date().toISOString(),
      lastModifiedDate: new Date().toISOString(),
      isDeleted: false,
      maxSlotsPerDay: 0,
      title: '',
    } as SlotConfiguration;

    setData([...data, newConfig]);
    // set new config to editing mode
    setEditingId(newConfig.feId);
  };

  const handleDelete = async (slotConfig: SlotConfiguration) => {
    slotConfig.isDeleted = true;

    if (slotConfig.id <= 0) {
      setData(data.filter((x) => x.feId !== slotConfig.feId));
      return;
    }

    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await postAppointmentsLimitConfiguration(token.accessToken, [slotConfig]);
    if (res.isSuccess) {
      notification.success({ message: 'Deleted' });
      loadData();
    } else {
      notification.error({ message: 'Delete failed', description: res.errors?.join('\n') });
    }
  };

  const confirmDelete = (slotConfig: SlotConfiguration) => {
    Modal.confirm({
      title: 'Confirm Delete',
      content: 'Are you sure you want to delete this slot configuration?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => await handleDelete(slotConfig),
    });
  };

  return (
    <div className="container flex-admin-vertical-container" style={{ width: 900, justifyContent: 'flex-start' }}>
      <Row style={{ marginBottom: '8px' }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          Appointment Limits
        </Title>
        <Text type="secondary" style={{ display: 'block', marginTop: '5px' }}>
          Used to specify the maximum number of booked appointments a seller can have per day. The seller can be specified by a number of paramaters.
          <br />
          <em>
            {' '}
            Note: For the sales channel 'SalesCallTwo', only appointments with the status 'Reserved' or 'Customer Reached' count towards the daily appointment limit. For all other channels, all Sales
            Call 1 and Sales Call 2 appointments count towards the limit, regardless of status.
          </em>
        </Text>
      </Row>
      <Space style={{ width: '100%', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={onAddNewSlotConfig}>
          Add Appointment Limit
        </Button>
      </Space>

      <Space direction="vertical" style={{ width: '100%' }} size={24}>
        {data.map((item, index) => (
          <React.Fragment key={item.feId}>
            {editingId === item.feId ? (
              <SlotConfigForm onSave={onSubmit} onCancel={setEditingId} onDelete={handleDelete} slotConfiguration={item} />
            ) : (
              <Card
                styles={{
                  header: { backgroundColor: 'var(--card-header-bg)' },
                }}
                title={<span>{item.title || 'No Title'}</span>}
                extra={
                  <Space>
                    <Button type="default" icon={<EditOutlined />} onClick={() => setEditingId(item.feId)}>
                      Edit
                    </Button>
                    <Button type="default" icon={<DeleteOutlined />} onClick={() => confirmDelete(item)}>
                      Delete
                    </Button>
                  </Space>
                }
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px',
                  }}
                >
                  <span>
                    <span style={{ color: 'var(--text-secondary)' }}>Max Appointments:</span>
                    <span style={{ marginLeft: '4px', fontWeight: 500 }}>{item.maxSlotsPerDay || '-'}</span>
                  </span>
                  <span>
                    <span style={{ color: 'var(--text-secondary)' }}>Sales League:</span>
                    <span style={{ marginLeft: '4px', fontWeight: 500 }}>{item.salesLeague || '-'}</span>
                  </span>
                  <span>
                    <span style={{ color: 'var(--text-secondary)' }}>Sales Channel:</span>
                    <span style={{ marginLeft: '4px', fontWeight: 500 }}>{!item.salesChannel || item.salesChannel === SalesChannel.MissingQueue ? '-' : item.salesChannel}</span>
                  </span>
                  <span>
                    <span style={{ color: 'var(--text-secondary)' }}>Product Type:</span>
                    <span style={{ marginLeft: '4px', fontWeight: 500 }}>{!item.productType || item.productType === ProductType.UNKNOWN ? '-' : item.productType}</span>
                  </span>
                  <span>
                    <span style={{ color: 'var(--text-secondary)' }}>Payment Model:</span>
                    <span style={{ marginLeft: '4px', fontWeight: 500 }}>{!item.paymentModel || item.paymentModel === 'ANY' ? '-' : item.paymentModel}</span>
                  </span>
                  <span>
                    <span style={{ color: 'var(--text-secondary)' }}>Department:</span>
                    <span style={{ marginLeft: '4px', fontWeight: 500 }}>{!item.department || item.department === 'None' ? '-' : item.department}</span>
                  </span>
                </div>
              </Card>
            )}
            {index < data.length - 1 && <Divider dashed style={{ margin: 0 }} />}
          </React.Fragment>
        ))}
      </Space>
    </div>
  );
};
